import { ReactNode, MouseEvent } from 'react';
import { Box, Divider, Paper, Tooltip } from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DeleteIcon from '@mui/icons-material/Delete';
import TimelineIcon from '@mui/icons-material/Timeline';
import { IconExpression } from '../Icons';
import { TransitionIconButton } from './TransitionIconButton';

type Props = {
	label?: ReactNode;
	hasCondition: boolean;
	isAgentTool: boolean;
	selected: boolean;
	onChangeType: (event: MouseEvent<HTMLElement>) => void;
	onCopy: () => void;
	onRemove: () => void;
};

/** Indicators stay readable (not disabled buttons); editing actions appear only on selection. */
export function TransitionEdgeToolbar({ label, hasCondition, isAgentTool, selected, onChangeType, onCopy, onRemove }: Props) {
	const hasIndicators = hasCondition || isAgentTool;
	return <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
		{label && <Box className="label" sx={{ maxWidth: 180, borderRadius: 1, px: 0.75, py: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Box>}
		{(hasIndicators || selected) && <Paper variant="outlined" role="group" aria-label="Transition indicators and actions"
			onPointerDown={event => event.stopPropagation()} onDoubleClick={event => event.stopPropagation()}
			sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', borderRadius: 1.5, bgcolor: 'background.paper',
				borderColor: selected ? 'primary.main' : 'divider', boxShadow: selected ? 2 : 1, overflow: 'hidden' }}>
			{hasIndicators && <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', px: 0.5, py: 0.25, gap: 0.25 }}>
				{hasCondition && <Tooltip title="Condition expression present">
					<Box component="span" role="img" aria-label="Condition expression present" sx={{ display: 'flex', p: 0.25, color: 'info.main', '& svg': { fontSize: 16 } }}><IconExpression /></Box>
				</Tooltip>}
				{hasCondition && isAgentTool && <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />}
				{isAgentTool && <Tooltip title="Agent tool transition">
					<Box component="span" role="img" aria-label="Agent tool transition" sx={{ display: 'flex', p: 0.25, color: 'primary.main', '& svg': { fontSize: 16 } }}><BuildIcon /></Box>
				</Tooltip>}
			</Box>}
			{selected && <>
				{hasIndicators && <Divider />}
				<Box sx={{ display: 'flex', alignItems: 'center', p: 0.25, gap: 0.25 }}>
					<TransitionIconButton title="Change edge type" color="default" onClick={onChangeType}><TimelineIcon /></TransitionIconButton>
					<TransitionIconButton title="Copy Data as JSON" onClick={onCopy}><ContentPasteIcon /></TransitionIconButton>
					<Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
					<TransitionIconButton title="Remove transition" color="error" onClick={onRemove}><DeleteIcon /></TransitionIconButton>
				</Box>
			</>}
		</Paper>}
	</Box>;
}
